import { InputText } from 'primereact/inputtext';
import { Control, Controller, FieldValues, Path, PathValue, RegisterOptions } from 'react-hook-form';
import { TextBodyExtraExtraSmall, TextBodyExtraSmall2, TextBodyLarge } from '@/components/text/Texts';
import IdGeneratorService from '@/services/id-generator.service';
import Container, { Column, Row } from '@/components/container/Container';
import { MdLocationOn } from "react-icons/md";
import { useCallback, useEffect, useState } from 'react';
import { placesRepository } from '../views/PlacesRepository';
import { X4bLocation } from '../views/PlacesRepository';
import { debounce } from 'lodash';
import { showError } from '@/modules/notification/engine/notification.library';
import { cn } from '@/components/ui/cn';

interface Props<T extends FieldValues> {
	id: Path<T>;
	control: Control<T>;
	formName: string;
	label?: string;
	rules?: RegisterOptions<T, Path<T>>;
	defaultValue?: PathValue<T, Path<T>>;
}

const AddressInput = <T extends FieldValues>({
	id,
	control,
	formName,
	label,
	rules,
	defaultValue,
}: Props<T>) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = idService.generateFormInputIds(id);

	const [isFocus, setIsFocus] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [addressList, setAddressList] = useState<X4bLocation[]>([]);

	const debouncedSearch = useCallback(
		debounce(async (value: string) => {
			if (value.trim().length == 0) setAddressList([])
			else {
				try {
					const result = await placesRepository.searchPlaces(value);
					setAddressList(result);
				} catch (error) {
					showError('Failed to fetch addresses', { summary: 'Error' });
				}
			}
		}, 300), []
	);

	useEffect(() => {
		debouncedSearch(searchValue);
		return () => debouncedSearch.cancel();
	}, [searchValue, debouncedSearch]);

	return (
		<Controller
			control={control}
			name={id}
			rules={rules}
			defaultValue={defaultValue}
			render={({ field, fieldState: { error } }) => {
				// Sync searchValue with field value when it changes externally (e.g., from map selection)
				useEffect(() => {
					const location = field.value as X4bLocation | undefined;
					if (location?.Address !== searchValue) {
						setSearchValue(location?.Address || '');
					}
				}, [field.value]);

				const hasValue = !!searchValue;

				return (
					<Column>
						<Container className="relative">
							<InputText
								id={idGenerated.id}
								// using to avoid browser autocomplete. off value is not working
								autoComplete="new-password"
								value={searchValue}
								onChange={(e) => {
									setSearchValue(e.target.value);
									if (field.value && e.target.value !== field.value.address)
										field.onChange(undefined);
								}}
								onFocus={() => setIsFocus(true)}
								onBlur={() => {
									setIsFocus(false);
									field.onBlur();
								}}
								className={cn(
									'w-full h-[56px] px-4 pt-6 pb-2 rounded-t-[4px] bg-surfaceDim duration-200',
									'appearance-none focus:outline-none focus:ring-0 shadow-none outline-none resize-none',
									'font-poppins text-body-large text-onSurface',
									// focus and error state
									'border-none bg-bottom bg-origin-border bg-no-repeat',
									'bg-[size:0_3px,_100%_1px]', isFocus && 'focus:bg-[size:100%_3px,_100%_1px]',
									error // error60, primary60, neutralVariant20 - colors
										? 'bg-[linear-gradient(to_bottom,#eb4236ff,#eb4236ff),linear-gradient(to_bottom,#eb4236ff,#eb4236ff)]'
										: 'bg-[linear-gradient(to_bottom,#004060ff,#004060ff),linear-gradient(to_bottom,#2f3639ff,#2f3639ff)]',
								)}
							/>
							<label
								htmlFor={idGenerated.id}
								children={label ?? idService.generateLabel(id)}
								className={cn(
									'absolute z-10 origin-[0] left-4',
									'transition-all duration-200 pointer-events-none',
									'font-poppins text-body-large',
									error ? 'text-error60' : isFocus ? 'text-primary60' : 'text-neutralVariant20',
									(isFocus || hasValue) ? 'scale-[78%]' : 'translate-y-1/2 scale-100',
								)}
							/>

							{isFocus && (
								<Column
									onMouseDown={e => e.preventDefault()}
									className={cn(
										'absolute top-[100%] z-50 w-full gap-1 py-2 bg-surfaceContainer',
										'shadow-[0px_2px_6px_2px_rgba(0,0,0,0.15),0px_1px_2px_0px_rgba(0,0,0,0.30)]',
										'max-h-[300px] overflow-y-auto no-scrollbar'
									)}
								>
									{addressList.length === 0 && (
										<Row className="h-14 px-3 items-center">
											<TextBodyLarge text="No results" className="text-onSurface" />
										</Row>
									)}
									{addressList.length > 0 && addressList.map((address, i) => (
										<Row
											key={`address-${i}`}
											className="h-14 gap-3 px-3 items-center cursor-pointer duration-300 hover:bg-[#DBDEE0]"
											onClick={() => {
												field.onChange(address);
												setSearchValue(address.Address);
												const inputElement = document.getElementById(idGenerated.id);
												if (inputElement) inputElement.blur();
											}}
										>
											<MdLocationOn size={24} className="text-primary" />
											<Column className="w-[calc(100%-(24px+12px))]"> {/* Icon Size + Gap */}
												<TextBodyLarge text={address.PlaceName} className="text-onSurface whitespace-nowrap truncate" />
												<TextBodyExtraSmall2 text={address.Address} className="text-neutralVariant20 whitespace-nowrap truncate" />
											</Column>
										</Row>
									))}
								</Column>
							)}
						</Container>

						{(error) && (
							<TextBodyExtraExtraSmall
								id={`${idGenerated.id}-helper`}
								className="block pl-[16px] mt-1 text-error"
								text={error?.message ?? ''}
							/>
						)}
					</Column>
				);
			}}
		/>
	);
};

export default AddressInput;
